import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { getRoleHomeRoute, getUnauthorizedRedirect } from '../../guards/page-access.util';
import { ApiService } from '../../services/index';
import { pageModule, PageRoute, replaceIdWithPath } from '../../types/page.types';
import { useAppQuery } from '../use-app-query.hook';
import { useSelected } from './use-selected';

const ROUTES_ALLOWED_WITHOUT_SELECTED_COMPANY = new Set<string>([
  PageRoute.ALL_DONE,
  PageRoute.BILLING,
  PageRoute.COMPANY_SETUP,
  PageRoute.HANG_TIGHT,
  PageRoute.HOME,
  PageRoute.LIBRARY,
  PageRoute.MY_COMPANIES,
  PageRoute.ONBOARDING,
  PageRoute.PERKS,
  PageRoute.PROFILE,
  PageRoute.SETTINGS,
  PageRoute.WELCOME,
]);

export const isRouteAllowedWithoutSelectedCompany = (pathname: string): boolean =>
  ROUTES_ALLOWED_WITHOUT_SELECTED_COMPANY.has(replaceIdWithPath(pathname));

export const useCheckModuleAccess = (enabled?: boolean) => {
  const navigate = useNavigate();
  const { selectedCompany, selectedCompanyId: id, isLoading } = useSelected();
  const location = useLocation();
  const module = useMemo(
    () => pageModule[replaceIdWithPath(location.pathname)],
    [location.pathname],
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const companyIdFromUrl = new URLSearchParams(location.search).get('companyId');
    if (
      !isLoading &&
      !id &&
      !companyIdFromUrl &&
      !isRouteAllowedWithoutSelectedCompany(location.pathname)
    ) {
      navigate(PageRoute.MY_COMPANIES);
    }
  }, [enabled, id, isLoading, location.pathname, location.search, navigate]);

  const isPathDenied = Boolean(
    selectedCompany && getUnauthorizedRedirect(location.pathname, selectedCompany),
  );

  const { isLoading: isChecking } = useAppQuery({
    queryKey: [QueryKey.CHECK_MODULE_ACCESS, module, id],
    queryFn: () =>
      ApiService.get({
        endpoint: BackendRoute.COMPANIES,
        routePath: [id, 'check-permissions', module as string],
      }),
    enabled: !!module && !!enabled && !!id && !isLoading && !isPathDenied,
    defaultErrorHandling: false,
    onError: () =>
      navigate(selectedCompany ? getRoleHomeRoute(selectedCompany) : PageRoute.MY_COMPANIES),
  });

  return { isChecking };
};
